import React, {FC} from "react";
import {Table, Tag, Tooltip} from "antd";
import {red} from "@ant-design/colors";
import {TransferBulkPlanPreviewQuery, TransferOldBookingTreatment} from "../../../generated/graphql";
import {CurrencyShort, renderAmount} from "../../../helpers/renderAmount";
import i18n from "../../../services/i18n";
import {dueOf} from "../../bookings/TransferWizard/TransferCostCard";

/**
 * Vorschau-Tabelle der Bulk-Umbuchung (#666): eine Zeile je Buchung mit Kurspreis, übernommenen
 * Posten, gezahlt, angewandt, Überschuss, offen auf der neuen Buchung, Behandlung der alten
 * Buchung, Abrechnung (sofort/später), Hinweisen und Blockern. Reine Funktion ihrer Props — Laden
 * und Geldweg liegen im `BulkTransferWizard`.
 *
 * „Offen neu" rechnet wie der Einzel-Wizard (`dueOf`): Kurspreis + übernommene Posten −
 * verrechnetes Guthaben − Übertrag. Ohne die Posten-Spalte las sich „Gezahlt 40 · Angewandt 40"
 * wie ein Abzug vom Kurspreis (Abnahme 24.09.2026) — die 40 € hängen aber an den Posten.
 */

export type BulkRow = TransferBulkPlanPreviewQuery["transferBulkPlanPreview"]["rows"][number];

const money = (amount: number, currency?: CurrencyShort): string => renderAmount({amount: Math.round(amount * 100) / 100, currency});

export const treatmentLabel = (treatment: TransferOldBookingTreatment): string => {
    const tr = i18n.containers.bookings.TransferEngineConfirm;

    if (treatment === TransferOldBookingTreatment.Delete) return tr.oldDeleted();
    if (treatment === TransferOldBookingTreatment.CancelWithReceipt) return tr.oldCanceledWithReceipt();

    return tr.oldCanceledWithoutReceipt();
};

/**
 * „Rechnung sofort" je Zeile: die Wahl des Schritts Kommunikation (`true`/`false`) über dem
 * Default der Zeile (`null` = Regel des Servers: sofort, wenn A abgerechnet war); nie für die
 * Warteliste — dieselbe Kaskade wie `resolveCommunication` im Einzel-Wizard.
 */
export const invoiceNowFor = (row: Pick<BulkRow, "plan">, invoiceOverride: boolean | null, waitlist: boolean): boolean =>
    !waitlist && (invoiceOverride ?? row.plan.communicationDefaults?.createInvoiceBNow ?? false);

export const blockedRows = (rows: Array<Pick<BulkRow, "plan">>): number => rows.filter((row) => row.plan.blockers.length > 0).length;

const carriedSum = (row: BulkRow): number => row.plan.carriedOverItems.reduce((sum, item) => sum + item.amount, 0);
/** Offen auf der neuen Buchung nach dem Übertrag, nie negativ (Überschuss). */
export const openAfterTransfer = (row: BulkRow): number => Math.max(0, Math.round(dueOf(row.plan) * 100) / 100);

const BulkTransferPreviewTable: FC<{rows: Array<BulkRow>; currency?: CurrencyShort; invoiceOverride: boolean | null; waitlist: boolean}> = ({rows, currency, invoiceOverride, waitlist}) => {
    const tr = i18n.containers.bookings.TransferWizard.bulk.preview;
    const sum = (pick: (row: BulkRow) => number): number => rows.reduce((acc, row) => acc + pick(row), 0);
    const columns = [
        {title: tr.booking(), dataIndex: "sourceBookingNumber", key: "booking"},
        {title: tr.attendee(), dataIndex: "attendeeName", key: "attendee"},
        {title: tr.priceB(), key: "priceB", align: "right" as const, render: (_: unknown, row: BulkRow) => money(row.plan.priceB, currency)},
        {
            title: tr.carriedItems(),
            key: "carriedItems",
            align: "right" as const,
            render: (_: unknown, row: BulkRow) =>
                row.plan.carriedOverItems.length > 0 ? (
                    <Tooltip title={<ul style={{paddingLeft: 16, marginBottom: 0}}>{row.plan.carriedOverItems.map((item, index) => <li key={index}>{`${item.title ?? tr.untitledItem()}: ${money(item.amount, currency)}`}</li>)}</ul>}>
                        <span data-carried-items>{`+ ${money(carriedSum(row), currency)}`}</span>
                    </Tooltip>
                ) : (
                    "–"
                ),
        },
        {title: tr.paidA(), key: "paidA", align: "right" as const, render: (_: unknown, row: BulkRow) => money(row.plan.paidA, currency)},
        {title: tr.appliedToB(), key: "appliedToB", align: "right" as const, render: (_: unknown, row: BulkRow) => money(row.plan.appliedToB, currency)},
        {title: tr.surplus(), key: "surplus", align: "right" as const, render: (_: unknown, row: BulkRow) => money(row.plan.surplus, currency)},
        {
            title: tr.openB(),
            key: "openB",
            align: "right" as const,
            render: (_: unknown, row: BulkRow) => {
                // Warteliste: nichts wird verrechnet, abgerechnet wird erst beim Nachrücken.
                if (waitlist) return <Tooltip title={tr.openWaitlist()}><span>–</span></Tooltip>;
                const amount = <b data-open-b>{money(openAfterTransfer(row), currency)}</b>;

                return row.plan.creditReapplied > 0 ? <Tooltip title={tr.openCreditNote({amount: money(row.plan.creditReapplied, currency)})}>{amount}</Tooltip> : amount;
            },
        },
        {title: tr.oldBooking(), key: "oldBooking", render: (_: unknown, row: BulkRow) => <Tooltip title={row.plan.oldBooking.reason}>{treatmentLabel(row.plan.oldBooking.treatment)}</Tooltip>},
        {title: tr.invoiceB(), key: "invoiceB", render: (_: unknown, row: BulkRow) => (invoiceNowFor(row, invoiceOverride, waitlist) ? tr.invoiceNow() : tr.invoiceLater())},
        {
            title: tr.hints(),
            key: "hints",
            render: (_: unknown, row: BulkRow) =>
                row.plan.hints.length > 0 ? (
                    <Tooltip title={<ul style={{paddingLeft: 16, marginBottom: 0}}>{row.plan.hints.map((hint, index) => <li key={index}>{hint}</li>)}</ul>}>
                        <Tag>{row.plan.hints.length}</Tag>
                    </Tooltip>
                ) : (
                    "–"
                ),
        },
        {
            title: tr.blockers(),
            key: "blockers",
            render: (_: unknown, row: BulkRow) =>
                row.plan.blockers.length > 0 ? <ul style={{paddingLeft: 16, marginBottom: 0, color: red[5]}}>{row.plan.blockers.map((blocker, index) => <li key={index}>{blocker}</li>)}</ul> : "–",
        },
    ];

    return (
        <Table
            size="small"
            pagination={false}
            rowKey="sourceBookingId"
            dataSource={rows}
            columns={columns}
            summary={() => (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><b>{tr.total()}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{rows.length}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">{money(sum((row) => row.plan.priceB), currency)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">{sum(carriedSum) > 0 ? `+ ${money(sum(carriedSum), currency)}` : "–"}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">{money(sum((row) => row.plan.paidA), currency)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">{money(sum((row) => row.plan.appliedToB), currency)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">{money(sum((row) => row.plan.surplus), currency)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">{waitlist ? "–" : <b>{money(sum(openAfterTransfer), currency)}</b>}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8} colSpan={4}>{blockedRows(rows) > 0 ? <span style={{color: red[5]}}>{tr.blockedRows({count: String(blockedRows(rows)), total: String(rows.length)})}</span> : null}</Table.Summary.Cell>
                </Table.Summary.Row>
            )}
        />
    );
};

export default BulkTransferPreviewTable;
